/**
 * KeyboardShortcuts Hook
 * Handles keyboard shortcuts for the dev tool panel
 * 
 * Features:
 * - Escape to close panel
 * - Cmd/Ctrl + \ to toggle panel open/closed
 * - Tab navigation between tabs when panel is focused
 * - Prevents shortcuts when typing in inputs
 * - Configurable shortcuts via data attributes
 */

const KeyboardShortcuts = {
  mounted() {
    // Configuration
    this.shortcuts = {
      toggle: { key: '\\', meta: true }, // Cmd/Ctrl + \
      close: { key: 'Escape' },
      nextTab: { key: 'Tab' },
      prevTab: { key: 'Tab', shift: true },
      focusPanel: { key: 'k', meta: true, shift: true } // Cmd/Ctrl + Shift + K
    };
    
    // State
    this.enabled = true;
    
    // Bind methods
    this.handleKeyDown = this.handleKeyDown.bind(this);
    
    // Add global keyboard listener
    document.addEventListener('keydown', this.handleKeyDown);
    
    // Listen for enable/disable events
    this.handleEvent('shortcuts-enabled', () => this.enabled = true);
    this.handleEvent('shortcuts-disabled', () => this.enabled = false);
  },
  
  handleKeyDown(e) {
    if (!this.enabled) return;
    
    // Don't intercept shortcuts when typing in inputs
    if (this.isTyping(e)) return;
    
    // Check for toggle shortcut
    if (this.matchesShortcut(e, this.shortcuts.toggle)) {
      e.preventDefault();
      this.sendEvent('toggle-panel');
      return;
    }
    
    // Check for close shortcut
    if (this.matchesShortcut(e, this.shortcuts.close)) {
      // Only prevent default if panel is open
      if (this.isPanelOpen()) {
        e.preventDefault();
        this.sendEvent('close-panel');
      }
      return;
    }
    
    // Check for focus panel shortcut
    if (this.matchesShortcut(e, this.shortcuts.focusPanel)) {
      e.preventDefault();
      this.sendEvent('focus-panel');
      return;
    }
    
    // Tab navigation only works when panel is focused
    if (this.isPanelFocused()) {
      if (this.matchesShortcut(e, this.shortcuts.nextTab)) {
        e.preventDefault();
        this.sendEvent('next-tab');
      } else if (this.matchesShortcut(e, this.shortcuts.prevTab)) {
        e.preventDefault();
        this.sendEvent('prev-tab');
      }
    }
  },
  
  matchesShortcut(e, shortcut) {
    // Check key
    if (e.key !== shortcut.key) return false;
    
    // Check modifiers
    if (shortcut.meta && !(e.metaKey || e.ctrlKey)) return false;
    if (shortcut.shift && !e.shiftKey) return false;
    if (shortcut.alt && !e.altKey) return false;
    
    // Make sure no extra modifiers are pressed
    if (!shortcut.meta && (e.metaKey || e.ctrlKey)) return false;
    if (!shortcut.shift && e.shiftKey) return false;
    if (!shortcut.alt && e.altKey) return false;
    
    return true;
  },
  
  isTyping(e) {
    const target = e.target;
    const tagName = target.tagName.toLowerCase();
    
    // Check if target is an input element
    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
      return true;
    }
    
    // Check if target has contenteditable
    if (target.isContentEditable || target.contentEditable === 'true') {
      return true;
    }
    
    // Check for custom input components with data attributes
    if (target.closest('[data-input]') || target.closest('[contenteditable]')) {
      return true;
    }
    
    return false;
  },
  
  isPanelOpen() {
    // Check if the dev tool panel is currently open
    const panel = document.querySelector('[data-panel="dev-tool"]');
    return panel && !panel.classList.contains('panel-collapsed');
  },
  
  isPanelFocused() {
    // Check if focus is within the dev tool panel
    const panel = document.querySelector('[data-panel="dev-tool"]');
    return panel && panel.contains(document.activeElement);
  },
  
  sendEvent(eventName) {
    // Emit custom event that the main panel hook can listen to
    this.el.dispatchEvent(new CustomEvent('keyboard-shortcut', {
      bubbles: true,
      detail: { action: eventName }
    }));
    
    // Also push to LiveView if needed
    this.pushEvent(eventName.replace('-', '_'), {});
  },
  
  destroyed() {
    // Clean up event listener
    document.removeEventListener('keydown', this.handleKeyDown);
  }
};

export default KeyboardShortcuts;